import React, { useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, Link, NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { TrendingUp, Calendar, Building2, User, Package, LogOut, ArrowLeft } from 'lucide-react';
import { ADMIN_ICON_STROKE } from '../admin/AdminShared';

const vendorNavItems = [
  { to: '/vendor/growth', label: 'Growth', icon: TrendingUp, isActive: (p) => p === '/vendor' || p.startsWith('/vendor/growth') },
  { to: '/vendor/bookings', label: 'Bookings', icon: Calendar, isActive: (p) => p.startsWith('/vendor/bookings') },
  { to: '/vendor/listings', label: 'Listings', icon: Building2, isActive: (p) => p.startsWith('/vendor/listings') },
  { to: '/vendor/inventory', label: 'Inventory', icon: Package, isActive: (p) => p.startsWith('/vendor/inventory') },
  { to: '/vendor/accounts', label: 'Accounts', icon: User, isActive: (p) => p.startsWith('/vendor/accounts') }
];

const navPillClass = (active) =>
  `whitespace-nowrap px-4 py-2 rounded-full text-[14px] font-medium transition-colors shrink-0 inline-flex items-center gap-2 ${
    active ? 'bg-black text-white' : 'bg-white text-black border border-[#E5E3DF] hover:bg-[#FAF7F2]'
  }`;

const VendorLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, isAuthenticated, logout } = useAuth();

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/vendor/login', { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated || !user) {
        navigate('/vendor/login', { replace: true });
        return;
      }
      if (user.role !== 'vendor' && user.role !== 'admin') {
        navigate('/vendor/login', { replace: true, state: { needsVendorAccount: true } });
        return;
      }
    }
  }, [user, loading, isAuthenticated, navigate]);

  if (loading || !isAuthenticated || !user || (user.role !== 'vendor' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const displayName = user?.name?.trim() || user?.phone || user?.email || 'Vendor';
  const pathname = location.pathname;
  const isVendorHub = pathname === '/vendor' || pathname === '/vendor/';

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <header className="bg-white border-b border-[#E5E3DF] sticky top-0 z-40 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
        <div className="max-w-6xl mx-auto w-full px-5 md:px-[120px] py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 min-w-0">
              {!isVendorHub && (
                <Link
                  to="/vendor/growth"
                  className="inline-flex items-center justify-center p-2 rounded-full bg-[#FAF7F2] text-[#FF5E4C] border border-[#E5E3DF] hover:bg-[#f5f0e8] shrink-0 relative z-50"
                  aria-label="Back to vendor home"
                >
                  <ArrowLeft className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} aria-hidden />
                </Link>
              )}
              <Link
                to="/"
                className="text-[16px] font-medium text-black tracking-[-0.02em] hover:opacity-80 truncate"
              >
                Book For Paws
              </Link>
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FAF7F2] text-[#5c5c5c] border border-[#E5E3DF] shrink-0">
                Vendor
              </span>
            </div>
            <div className="flex items-center gap-3 text-[13px] shrink-0">
              <span className="text-[#78736A] truncate max-w-[160px]">{displayName}</span>
              <Link to="/" className="font-medium text-[#FF5E4C] hover:opacity-90">
                View site
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium text-black border border-[#E5E3DF] bg-white hover:bg-[#FAF7F2]"
                aria-label="Log out"
              >
                <LogOut className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                Log out
              </button>
            </div>
          </div>
          <nav className="hidden md:flex gap-2 overflow-x-auto pb-0.5 -mx-1 px-1 [scrollbar-width:thin]">
            {vendorNavItems.map((item) => {
              const Icon = item.icon;
              const active = item.isActive(pathname);
              return (
                <NavLink key={item.to} to={item.to} className={() => navPillClass(active)}>
                  <Icon className="w-4 h-4 shrink-0" strokeWidth={ADMIN_ICON_STROKE} aria-hidden />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-5 md:px-[120px] py-6 md:py-8 pb-24 md:pb-12">
        <Outlet />
      </main>

      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E3DF] shadow-[0_-4px_12px_rgba(0,0,0,0.06)] z-50">
        <div className="flex justify-around items-center h-16 max-w-6xl mx-auto">
          {vendorNavItems.map((item) => {
            const Icon = item.icon;
            const active = item.isActive(pathname);
            return (
              <button
                key={item.to}
                type="button"
                onClick={() => navigate(item.to)}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  active ? 'text-[#FF5E4C]' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className={`w-5 h-5 mb-1 ${active ? 'text-[#FF5E4C]' : ''}`} strokeWidth={ADMIN_ICON_STROKE} />
                <span className={`text-xs font-medium ${active ? 'text-[#FF5E4C]' : ''}`}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VendorLayout;
